import { AirdropMementoIcon, BrightBoxIcon, LoadingIcon } from "components/atoms/Icon";
import { EyeSlashIcon } from "@heroicons/react/24/solid";
import { BanknotesIcon, MegaphoneIcon, ShoppingCartIcon } from "@heroicons/react/24/outline";
import { formatToCommaSeparatedString } from "utils/HelperFunctions";
import { FormEvent } from "react";
import { useRouter } from "components/hooks";
import Loading from "components/atoms/Loading";
import { isObjectEmpty } from "utils/helpers";
import { useAnalyticsDataProviderContext } from "./AnalyticsDataProvider";
import MetricCard from "./Components/MetricCard/MetricCard";
import { ROUTES } from "../../constants";
import UserAvatarForAnalytics from "./Components/UserAvatarForAnalytics";
import Carousel from "./Components/Carousel";
import BarGraph from "./Components/Graph/BarGraph";
import TopMementosTable from "./Components/TopMementosTable";
import TopReleasesTable from "./Components/TopReleasesTable";

function MagnetAnalyticsSection({
  magnetId,
  isBrandAnalyticsDataLoadingError,
  magnetAnalyticsData,
  isMagnetAnalyticsDataLoading,
  magnetAnalyticsTopMementosData,
}: {
  magnetId: string;
  isBrandAnalyticsDataLoadingError: boolean;
  magnetAnalyticsData: BrandAnalyticsData;
  isMagnetAnalyticsDataLoading: boolean;
  magnetAnalyticsTopMementosData: TopMementosData[];
}) {
  if (!magnetId) {
    return (
      <div className="mx-auto flex h-[80%] w-full items-center justify-center font-satoshi text-white">
        <p className="rounded-lg border-1 border-dashed p-10 px-14 text-center opacity-60">
          Select one of your Magnets from the dropdown menu above.
        </p>
      </div>
    );
  }

  if (isMagnetAnalyticsDataLoading) {
    return (
      <div className="relative left-1/2 top-1/3 w-60 -translate-x-1/2">
        <LoadingIcon color="#586c83" />
      </div>
    );
  }

  if (isBrandAnalyticsDataLoadingError) {
    return (
      <div className="mx-auto flex h-[80%] w-full items-center justify-center font-satoshi text-white">
        <p className="rounded-lg border-1 border-dashed p-10 px-14 text-center opacity-60">
          Something went wrong. Please try Again!
        </p>
      </div>
    );
  }

  if (!magnetAnalyticsData) {
    return null;
  }

  return (
    <div className="text-white">
      <div className="mb-8">
        <h2 className="-ml-2 mt-2 text-3xl font-bold">Magnet Metrics</h2>
        <div className="flex flex-wrap gap-10">
          <MetricCard
            metricName="Total Mementos"
            metricData={magnetAnalyticsData.total_mementos}
            icon={<AirdropMementoIcon className="mt-8 h-14" color="text-secondary" />}
          />
          <MetricCard
            metricName="Total Releases"
            metricData={magnetAnalyticsData.total_releases}
            icon={<MegaphoneIcon className="mt-6 h-16 text-secondary" />}
          />
          <MetricCard
            metricName="Private Releases"
            metricData={magnetAnalyticsData.total_event_releases}
            icon={<EyeSlashIcon className="mr-2 mt-6 h-14 text-secondary" />}
          />
          <MetricCard
            metricName="Magnets Sold"
            metricData={magnetAnalyticsData.total_sold}
            icon={<ShoppingCartIcon className="mt-4 h-16 text-secondary" />}
          />
          <MetricCard
            metricName="Mementos Dropped"
            metricData={magnetAnalyticsData.total_mementos_dropped}
            icon={<BrightBoxIcon className="mt-4 h-16" color="text-secondary" />}
          />
          <MetricCard
            metricName="Revenue"
            metricData={formatToCommaSeparatedString(magnetAnalyticsData.total_revenue, "currency")}
            icon={<BanknotesIcon className="mt-4 h-16 text-secondary" />}
          />
        </div>
      </div>
      <div className="">
        <h2 className="-mr-2 mt-2 text-3xl font-bold">Audience Metrics</h2>
        <div className="flex flex-wrap gap-10">
          <MetricCard
            metricName="Total Users"
            metricData={magnetAnalyticsData.total_mementos ? magnetAnalyticsData.total_users_count : "--"}
            className="bg-[#081727]"
          />
          <MetricCard
            metricName="Top Memento Owner"
            metricData={
              isObjectEmpty(magnetAnalyticsData.top_memento_owner) ? (
                "--"
              ) : (
                <UserAvatarForAnalytics
                  email={magnetAnalyticsData.top_memento_owner.email || ""}
                  username={magnetAnalyticsData.top_memento_owner.username || ""}
                  profileImageURL={magnetAnalyticsData.top_memento_owner.profile_image_url || ""}
                  additionalSubtext={`Mementos Owned: ${magnetAnalyticsData.top_memento_owner.mementos_owned}`}
                />
              )
            }
            className="bg-[#081727]"
          />
          <MetricCard
            metricName="Avg. Mementos Owned"
            metricData={magnetAnalyticsData.total_mementos ? magnetAnalyticsData.average_mementos_owned : "--"}
            className="bg-[#081727]"
          />
        </div>
      </div>
      <div className="mt-10">
        <Carousel
          title={
            <h2 className="-mr-2 mt-8 snap-y snap-mandatory font-satoshi text-3xl font-bold underline">
              Graphical Visualizations
            </h2>
          }
          isPreviewHorizontal
          key={magnetId}
        >
          <BarGraph
            data={magnetAnalyticsData?.graph_data?.brand_activity}
            labels={magnetAnalyticsData?.graph_data?.labels}
            yTitle="Count"
            title="Brand Activity Insights"
          />
          <BarGraph
            data={magnetAnalyticsData?.graph_data?.memento_insights}
            labels={magnetAnalyticsData?.graph_data?.labels}
            yTitle="Count"
            title="Memento Insights"
          />
          <BarGraph
            data={magnetAnalyticsData?.graph_data?.user_metrics}
            labels={magnetAnalyticsData?.graph_data?.labels}
            yTitle="Count"
            title="User Insights"
          />
          <BarGraph
            data={magnetAnalyticsData?.graph_data?.revenue_metrics}
            labels={magnetAnalyticsData?.graph_data?.labels}
            yTitle="Revenue (in $)"
            title="Revenue Insights"
          />
        </Carousel>
      </div>
      {magnetAnalyticsTopMementosData && !!magnetAnalyticsTopMementosData.length && (
        <div>
          <h2 className="-mr-2 mb-3 mt-8 snap-y snap-mandatory font-satoshi text-3xl font-bold underline">
            Top Memento Insights
          </h2>
          <TopMementosTable data={magnetAnalyticsTopMementosData} />
        </div>
      )}
      {magnetAnalyticsData.releases_data && !!magnetAnalyticsData.releases_data.length && (
        <div className="mb-48">
          <h2 className="-mr-2 mb-3 mt-8 snap-y snap-mandatory font-satoshi text-3xl font-bold underline">
            Release Insights
          </h2>
          <TopReleasesTable data={magnetAnalyticsData.releases_data} />
        </div>
      )}
    </div>
  );
}

function MagnetAnalytics() {
  const {
    magnetId,
    brandMagnets,
    isBrandMagnetsLoading,
    magnetAnalyticsData,
    isMagnetAnalyticsDataLoading,
    isBrandAnalyticsDataLoadingError,
    magnetAnalyticsTopMementosData,
  } = useAnalyticsDataProviderContext();
  const router = useRouter();

  const handleMagnetClick = (e: FormEvent<HTMLSelectElement>) => {
    // if magnetId is present than only url will we replaced
    router.go(`${ROUTES.BRAND_DASHBOARD}/analytics/magnet?m=${e.currentTarget.value}`);
  };

  if (isBrandMagnetsLoading) {
    return <Loading />;
  }
  if (!brandMagnets.length) {
    return (
      <div className="mx-6 my-12 flex h-[88%] items-center justify-center rounded-lg border-1 border-dashed border-white border-opacity-90 font-satoshi font-medium text-white opacity-70">
        --- No Magnet created yet! Checkout this page to view insights about each of your Magnet ---
      </div>
    );
  }

  if (brandMagnets.length > 0) {
    return (
      <div className="h-full w-full pl-10 font-satoshi">
        <div className="float-right">
          <h2 className="mr-8 inline-block text-xl font-semibold text-white">Choose a Magnet to view metrics:</h2>
          <select
            onChange={handleMagnetClick}
            className="bg-primary text-sm text-white outline-none ring-0"
            defaultValue={magnetId || ""}
          >
            <option value="" hidden>
              ---Select Magnet---
            </option>
            {brandMagnets.map((magnet) => (
              <option value={magnet.id} key={`magnets-dropdown-${magnet.id}`} className="text-xxs font-semibold">
                {magnet.name}
              </option>
            ))}
          </select>
        </div>
        <MagnetAnalyticsSection
          isBrandAnalyticsDataLoadingError={isBrandAnalyticsDataLoadingError}
          magnetId={magnetId}
          magnetAnalyticsData={magnetAnalyticsData}
          isMagnetAnalyticsDataLoading={isMagnetAnalyticsDataLoading}
          magnetAnalyticsTopMementosData={magnetAnalyticsTopMementosData}
        />
      </div>
    );
  }
  return null;
}
export default MagnetAnalytics;
